import apiUrl from './api';
import { getWithAuth, postWithAuth } from '@/lib/httpAdapter';

/** One org a person can access -- their own row in that org, plus whether
 * it's the org their current session is authenticated as. Mirrors the
 * backend's OrgMembershipItem (idl/organization.py). */
export interface OrgMembershipItem {
  OrgUUID: string;
  OrgName?: string | null;
  UserUUID: string;
  Role?: string | null;
  IsActive: boolean;
  /** The individual GitHub logins this org's git_org_name resolves to
   * (lowercased) -- more than one for a Path A multi-org workspace
   * ("acme,acme-labs"). Match a raw login from searchGithubOrgs against
   * this list, not OrgName -- OrgName is a human-editable display string
   * and can't be relied on to equal any login. */
  GitOrgLogins?: string[];
  /** Provider account id of this org's git org. The GitHub App install link
   * needs it to preselect an account (`suggested_target_id`); a login string
   * has no equivalent, so without it the user lands on GitHub's account
   * picker. Absent for a workspace whose id was never captured. */
  GitOrgId?: string | null;
  /** The org's git org name with its real casing, for display. GitOrgLogins is
   * lowercased for matching; showing it beside a name rendered with real
   * casing reads as inconsistent. Match on GitOrgLogins, show this. */
  GitOrgName?: string | null;
}

export interface OrgCombiningInfo {
  OtherOrgMemberships: OrgMembershipItem[];
  LinkedOrgUUIDs: string[];
  Error: string | null;
}

const parseJsonBody = async (response: Response): Promise<unknown> => {
  const raw = await response.json();
  return typeof raw === 'string' ? JSON.parse(raw) : raw;
};

/**
 * The caller's own org memberships and which of them are currently
 * combined into their dashboard. Backs LinkedOrgsCard -- one call gives
 * both the list to render and the linked/not-linked state per row.
 */
export const fetchOrgCombiningInfo = async (): Promise<OrgCombiningInfo> => {
  try {
    const response = await getWithAuth(`${apiUrl}getUserInfo/`);
    if (!response.ok) {
      return { OtherOrgMemberships: [], LinkedOrgUUIDs: [], Error: `HTTP ${response.status}` };
    }
    const data = (await parseJsonBody(response)) as {
      OtherOrgMemberships?: OrgMembershipItem[] | null;
      LinkedOrgUUIDs?: string[] | null;
      Error?: string | null;
    };
    return {
      OtherOrgMemberships: data.OtherOrgMemberships ?? [],
      LinkedOrgUUIDs: data.LinkedOrgUUIDs ?? [],
      Error: data.Error ?? null,
    };
  } catch (e) {
    return {
      OtherOrgMemberships: [],
      LinkedOrgUUIDs: [],
      Error: e instanceof Error ? e.message : 'Network error',
    };
  }
};

export interface SwitchActiveOrgResult {
  JWT: string | null;
  UserUUID: string | null;
  OrganizationUUID: string | null;
  OrgName: string | null;
  ExpiresIn: number | null;
  Error: string | null;
}

/**
 * Mint a fresh JWT for a sibling org identity -- become that org's own
 * native user. Distinct from linkOrgForCombining: this changes which org
 * the caller is authenticated as, rather than widening what their current
 * identity's dashboards read.
 */
export const switchActiveOrg = async (
  targetOrgUUID: string
): Promise<SwitchActiveOrgResult> => {
  try {
    const response = await postWithAuth(`${apiUrl}switchActiveOrg/`, {
      TargetOrgUUID: targetOrgUUID,
    });
    const data = (await parseJsonBody(response)) as Partial<SwitchActiveOrgResult>;
    return {
      JWT: data.JWT ?? null,
      UserUUID: data.UserUUID ?? null,
      OrganizationUUID: data.OrganizationUUID ?? null,
      OrgName: data.OrgName ?? null,
      ExpiresIn: data.ExpiresIn ?? null,
      Error: data.Error ?? (response.ok ? null : `HTTP ${response.status}`),
    };
  } catch (e) {
    return {
      JWT: null,
      UserUUID: null,
      OrganizationUUID: null,
      OrgName: null,
      ExpiresIn: null,
      Error: e instanceof Error ? e.message : 'Network error',
    };
  }
};

export interface OnboardLinkedOrgResult {
  OrgUUID: string | null;
  MemberUserUUID: string | null;
  AlreadyOnboarded: boolean;
  Error: string | null;
}

/**
 * Onboard a brand-new org the caller is a verified GitHub member of, or
 * attach the caller to it if someone else already onboarded it. Returns
 * the org's UUID either way -- pass it straight to linkOrgForCombining.
 */
export const onboardLinkedOrg = async (
  anchorUserUUID: string,
  gitOrgLogin: string,
  gitOrgId?: string,
  orgName?: string
): Promise<OnboardLinkedOrgResult> => {
  try {
    const response = await postWithAuth(`${apiUrl}onboardLinkedOrg/`, {
      AnchorUserUUID: anchorUserUUID,
      GitOrgLogin: gitOrgLogin,
      ...(gitOrgId ? { GitOrgId: gitOrgId } : {}),
      ...(orgName ? { OrgName: orgName } : {}),
    });
    const data = (await parseJsonBody(response)) as Partial<OnboardLinkedOrgResult>;
    return {
      OrgUUID: data.OrgUUID ?? null,
      MemberUserUUID: data.MemberUserUUID ?? null,
      AlreadyOnboarded: data.AlreadyOnboarded ?? false,
      Error: data.Error ?? (response.ok ? null : `HTTP ${response.status}`),
    };
  } catch (e) {
    return {
      OrgUUID: null,
      MemberUserUUID: null,
      AlreadyOnboarded: false,
      Error: e instanceof Error ? e.message : 'Network error',
    };
  }
};

export interface CombiningActionResult {
  Success: boolean;
  Error: string | null;
}

/** Personally combine targetOrgUUID's data into the caller's own
 * dashboard, without changing which org they're authenticated as. */
export const linkOrgForCombining = async (
  targetOrgUUID: string
): Promise<CombiningActionResult> => {
  try {
    const response = await postWithAuth(`${apiUrl}linkOrgForCombining/`, {
      TargetOrgUUID: targetOrgUUID,
    });
    const data = (await parseJsonBody(response)) as Partial<CombiningActionResult>;
    return {
      Success: data.Success ?? false,
      Error: data.Error ?? (response.ok ? null : `HTTP ${response.status}`),
    };
  } catch (e) {
    return { Success: false, Error: e instanceof Error ? e.message : 'Network error' };
  }
};

/** The inverse of linkOrgForCombining. targetOrgUUID may be a previously
 * linked org, or the caller's own home org. */
export const unlinkOrgForCombining = async (
  targetOrgUUID: string
): Promise<CombiningActionResult> => {
  try {
    const response = await postWithAuth(`${apiUrl}unlinkOrgForCombining/`, {
      TargetOrgUUID: targetOrgUUID,
    });
    const data = (await parseJsonBody(response)) as Partial<CombiningActionResult>;
    return {
      Success: data.Success ?? false,
      Error: data.Error ?? (response.ok ? null : `HTTP ${response.status}`),
    };
  } catch (e) {
    return { Success: false, Error: e instanceof Error ? e.message : 'Network error' };
  }
};
